<script setup lang="ts">
import {computed, reactive, ref, useTemplateRef, watch, nextTick} from "vue";
import {RouterLink, useRouter} from "vue-router";
import {useConfirm} from "primevue/useconfirm";
import {useToast} from "primevue/usetoast";
import {useMainStore} from "@/Stores/store";
import {Button, Dialog, Drawer, Icon, InputText, Menu, ProgressSpinner} from "@/Components/Library";
import {AlphabetList, FilterBar, PageLoading, PageTitle} from "@/Components/Global";
import {
    deleteQuestion,
    duplicateQuestion,
    getQuestion,
    getQuestions as getQuestionsFromApi,
    restoreQuestion,
} from "@/Services/questions.ts";
import {useIntersectionObserver, useStorage, watchDebounced} from "@vueuse/core";
import {getAdminGroups} from "@/Services/groups";
import useEcho from "@/Composables/echo";
import LaunchHeader from "./Launch/LaunchHeader.vue";
import User from "@/Models/User";
import QuestionItem from "./QuestionItem.vue";
import PageContainer from "@/Components/Global/PageContainer.vue";
import QuestionsIndexFilters from "@/Pages/User/Questions/QuestionsIndexFilters.vue";
import ShareQuestionDialog from "@/Pages/User/Questions/ShareQuestionDialog.vue";
import {Question} from "@/Types/question";
import type {Tag} from "@/Types";
import QuestionsMassActions from "@/Pages/User/Questions/QuestionsMassActions.vue";

const router = useRouter();
const confirm = useConfirm();
const toast = useToast();
const $store = useMainStore();
const $user = new User();
const state = reactive({
    error: null,
    show: {
        filters: false,
        questionMenu: null,
        select: false,
        shareDialog: false,
    },
    limits: {
        responses_count: 100,
        groups_count: 10,
    },
    loading: {
        duplicatingQuestion: false,
    },
});
const sort = useStorage("questions.index.sort", router.currentRoute.value.query.launch ? "-updated_at" : "title");
const filters = useStorage(
    "questions.index.filters",
    {
        type: {
            post: false,
            word: false,
            text: false,
            multiple_choice: false,
            ranking: false,
            slider: false,
            weighting: false,
            annotation: false,
            voice: false,
            gatekeeper: false,
        },
        responses_count: [0, 100],
        groups_count: [0, 10],
        group_id: [],
        tags: [] as Tag[],
        strict_tags: false,
        created_at: null,
        publish_from: null,
        publish_to: null,
        state: "created" as "created" | "archived",
        ownership: "all" as "all" | "mine",
    },
    localStorage,
    {
        serializer: {
            read: (v: string) =>
                JSON.parse(v, (key, value) => {
                    if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}T/.test(value)) {
                        return new Date(value);
                    }
                    return value;
                }),
            write: (v: any) => JSON.stringify(v),
        },
    },
);

const search = ref<string | null>(null);
const selectedTagNames = computed(() => {
    return (filters.value.tags ?? [])
        .map((tag) => (typeof tag === "string" ? tag : tag?.name))
        .filter((tag): tag is string => !!tag);
});

const params = reactive({
    page: 0,
    filter: {
        search: search.value,
        group_id: filters.value.group_id,
        state: filters.value.state,
        type: Object.keys(filters.value.type).filter(
            (key) => filters.value.type[key as keyof typeof filters.value.type],
        ),
        tags: selectedTagNames.value,
        strict_tags: filters.value.strict_tags,
        responses_count: filters.value.responses_count,
        groups_count: filters.value.groups_count,
        created_at: filters.value.created_at,
        publish_from: filters.value.publish_from,
        publish_to: filters.value.publish_to,
        ownership: filters.value.ownership,
    },
    include: ["files", "stimuli.files.model"],
    sort: sort.value,
});

const questions = ref<Question[]>([]);
const massActionsRef = useTemplateRef("massActions");
const questionsByLetter = computed(() => {
    const questionsByLetter = new Map<string, any[]>();
    questions.value?.forEach((question) => {
        const letter = question.title[0].toUpperCase();
        if (!questionsByLetter.has(letter)) {
            questionsByLetter.set(letter, []);
        }
        questionsByLetter.get(letter).push(question);
    });
    return questionsByLetter;
});
const groups = ref([]);
const selectedQuestion = computed(() => questions.value.find((q) => q.id === state.show.questionMenu));
const showQuestionMenu = computed({
    get: () => !!state.show.questionMenu,
    set: (val) => (state.show.questionMenu = val),
});
const loading = ref(true);
const getQuestions = async () => {
    try {
        loading.value = true;
        const {data, total, meta} = await getQuestionsFromApi(params);

        if (params.page > 0) {
            questions.value = [...questions.value, ...data];
        } else {
            questions.value = data;
        }

        if (router.currentRoute.value.query.launch) {
            await nextTick();
            setSelectableQuestion();
        }

        state.limits.responses_count = meta.max_responses_count;
        if (filters.value.responses_count[1] > state.limits.responses_count) {
            filters.value.responses_count[1] = state.limits.responses_count;
        }
        state.limits.groups_count = meta.max_groups_count;
        if (filters.value.groups_count[1] > state.limits.groups_count) {
            filters.value.groups_count[1] = state.limits.groups_count;
        }

        loadMore.value = data.length && questions.value.length < total;
    } catch (e) {
        console.error(e);
        state.error = e.response?.data?.message || e.message || "An error occurred";
    } finally {
        loading.value = false;
    }
};

getAdminGroups().then(({data}) => {
    groups.value = data.map((group) => ({id: group.id, name: group.name}));
});

const totalActiveFilters = computed(() => {
    let count = 0;
    if (Object.values(filters.value.type).some((f) => f)) {
        count++;
    }

    if (filters.value.responses_count[0] > 0 || filters.value.responses_count[1] < state.limits.responses_count) {
        count++;
    }

    if (filters.value.groups_count[0] > 0 || filters.value.groups_count[1] < state.limits.groups_count) {
        count++;
    }

    if (filters.value.group_id?.length > 0) {
        count++;
    }

    if (filters.value.tags?.length > 0) {
        count++;

        if (filters.value.strict_tags) {
            count++;
        }
    }

    if (filters.value.created_at) {
        count++;
    }

    if (filters.value.publish_from) {
        count++;
    }

    if (filters.value.publish_to) {
        count++;
    }

    if (filters.value.state !== "created") {
        count++;
    }

    if (filters.value.ownership && filters.value.ownership !== "all") {
        count++;
    }

    return count;
});

const handleDuplicateQuestion = async (question: any, edit = false) => {
    try {
        state.show.questionMenu = null;
        state.loading.duplicatingQuestion = true;
        const {data} = await duplicateQuestion(question.id);

        if (params.sort === "title") {
            const insertIndex = questions.value.findIndex(
                (q) => q.title.localeCompare(data.title, undefined, {numeric: true, sensitivity: "base"}) > 0,
            );
            if (insertIndex === -1) {
                questions.value.push(data);
            } else {
                questions.value.splice(insertIndex, 0, data);
            }
        } else {
            questions.value.unshift(data);
        }

        toast.add({
            severity: "success",
            summary: "Duplicated",
            detail: "Post duplicated successfully.",
            life: 3000,
        });

        if (edit) {
            await router.push({name: "questions.edit", params: {question: data.id}});
        }
    } catch (e) {
        console.error(e);
    } finally {
        state.loading.duplicatingQuestion = false;
    }
};

const editMenuItem = {
    label: "Edit",
    icon: "prime:pencil",
    command: () => {
        if (selectedQuestion.value?.groups_count && !$user.hasRole("super-admin")) {
            const currentQuestion = selectedQuestion.value;
            confirm.require({
                header: "Editing a published post",
                message: "This post has already been published. Would you like to duplicate it?",
                acceptClass: "bg-askr-green text-white px-2 py-1 rounded-lg hover:brightness-110",
                rejectClass: "text-red-600 px-2 py-1 rounded-lg hover:brightness-110",
                accept: async () => {
                    await handleDuplicateQuestion(currentQuestion, true);
                },
            });
            state.show.questionMenu = null;
            return;
        }

        router.push({name: "questions.edit", params: {question: selectedQuestion.value?.id}});
    },
};

const duplicateMenuItem = {
    label: "Duplicate",
    icon: "prime:copy",
    command: async () => {
        await handleDuplicateQuestion(selectedQuestion.value);
        state.show.questionMenu = null;
    },
};

const shareMenuItem = {
    label: "Share",
    icon: "prime:share-alt",
    command: async () => {
        state.show.shareDialog = true;
    },
};

const archiveMenuItem = {
    label: "Archive",
    icon: "prime:trash",
    command: () => {
        // if (selectedQuestion.value.isPublished) {
        //     toast.add({
        //         severity: "warn",
        //         summary: "Warning",
        //         detail: "You cannot archive a question that has been published.",
        //         life: 3000,
        //     });
        //     state.show.questionMenu = null;
        //     return;
        // }

        const currentQuestion = selectedQuestion.value;
        if (!currentQuestion) {
            return;
        }

        confirm.require({
            header: "Archive post",
            message: `Are you sure you want to archive "${currentQuestion.title}"?`,
            acceptClass: "bg-askr-green text-white px-2 py-1 rounded-lg hover:brightness-110",
            rejectClass: "text-red-600 px-2 py-1 rounded-lg hover:brightness-110",
            accept: async () => {
                await deleteQuestion(currentQuestion.id);
                toast.add({
                    severity: "success",
                    summary: "Archived",
                    detail: "Post archived successfully.",
                    life: 3000,
                });
                questions.value = questions.value.filter((q) => q.id !== currentQuestion.id);
            },
        });
        state.show.questionMenu = null;
    },
    class: "text-red-600 hover:bg-red-100",
};

const unarchiveQuestionMenuItem = {
    label: "Restore",
    icon: "prime:upload",
    command: () => {
        const currentQuestion = selectedQuestion.value;
        if (!currentQuestion) {
            return;
        }

        confirm.require({
            header: "Restore post",
            message: `Are you sure you want to restore "${currentQuestion.title}"?`,
            acceptClass: "bg-askr-green text-white px-2 py-1 rounded-lg hover:brightness-110",
            rejectClass: "text-red-600 px-2 py-1 rounded-lg hover:brightness-110",
            accept: async () => {
                await restoreQuestion(currentQuestion.id);
                toast.add({
                    severity: "success",
                    summary: "Restored",
                    detail: "Post restored successfully.",
                    life: 3000,
                });
                questions.value = questions.value.filter((q) => q.id !== currentQuestion.id);
            },
        });
        state.show.questionMenu = null;
    },
};

const questionMenuItems = ref([
    {
        label: "Post",
        items: [],
    },
]);

const toggleQuestionMenu = (e, question) => {
    if (question.question.deleted_at) {
        questionMenuItems.value[0].items = [unarchiveQuestionMenuItem];
    } else {
        const items = [];
        if ($user.can("reuse questions")) {
            items.push(editMenuItem, duplicateMenuItem, shareMenuItem);
        }

        items.push(archiveMenuItem);

        questionMenuItems.value[0].items = items;
    }

    state.show.questionMenu = question.id;
    questionMenuItems.value[0].label = Str.of(selectedQuestion.value?.title ?? "Post").truncate().value;
};

const loadMore = ref(false);
const loadMoreIntersection = useTemplateRef("loadMoreIntersection");
const {stop} = useIntersectionObserver(
    loadMoreIntersection,
    ([{isIntersecting}], observerElement) => isIntersecting && loadMore.value && params.page++,
);

const applyFilters = () => {
    const newParams = {
        page: 0,
        filter: {
            ...params.filter,
            search: search.value,
            type: Object.keys(filters.value.type).filter(
                (key) => filters.value.type[key as keyof typeof filters.value.type],
            ),
            tags: selectedTagNames.value,
            strict_tags: filters.value.strict_tags,
            responses_count: filters.value.responses_count,
            groups_count: filters.value.groups_count,
            group_id: filters.value.group_id,
            created_at: filters.value.created_at,
            publish_from: filters.value.publish_from,
            publish_to: filters.value.publish_to,
            state: filters.value.state,
            ownership: filters.value.ownership,
        },
    };
    Object.assign(params, newParams);
    state.show.filters = false;
    deselectAll();
};

const clearFilters = () => {
    Object.assign(filters.value, {
        group_id: [],
        tags: [] as Tag[],
        strict_tags: false,
        type: {
            post: false,
            word: false,
            text: false,
            multiple_choice: false,
            ranking: false,
            slider: false,
            weighting: false,
            annotation: false,
            voice: false,
            gatekeeper: false,
        },
        responses_count: [0, 100],
        groups_count: [0, 10],
        created_at: null,
        publish_from: null,
        publish_to: null,
        state: "created" as "created" | "archived",
        ownership: "all" as "all" | "mine",
    });
    search.value = null;
    applyFilters();
};

const sortPanel = useTemplateRef("sortPanel");
const sortMenuItems = ref([
    {
        label: "Alphabetical",
        icon: "mdi:sort-alphabetical-ascending",
        value: "title",
        command: () => {
            sort.value = "title";
        },
    },
    {
        label: "Most recent",
        icon: "ic:round-edit-calendar",
        value: "-updated_at",
        command: () => {
            sort.value = "-updated_at";
        },
    },
    {
        label: "Least recent",
        icon: "ic:round-edit-calendar",
        value: "updated_at",
        command: () => {
            sort.value = "updated_at";
        },
    },
]);

const selectedSortOption = computed(() => {
    return sortMenuItems.value.find((item) => item.value === sort.value);
});

const launch = async () => {
    if (router.currentRoute.value.query.launch) {
        await router.push({name: "questions.launch"});
        return;
    }
    params.sort = "-updated_at";
    await router.replace({query: {launch: true}});
};

const select = async () => {
    $store.selectedQuestions = [];
    params.sort = "-updated_at";
    await router.replace({query: {select: true}});
};

const cancel = () => {
    $store.selectedQuestions = [];
    $store.launch.questions = [];
    $store.launch.user = null;
    router.push({query: {}});
};

const handleSharingStarted = () => {
    massActionsRef.value?.startSharingProcess();
};

const deselectAll = () => {
    $store.selectedQuestions = [];
    $store.launch.questions = [];
};

const launchingQuestions = computed<Question[]>(() =>
    questions.value.filter((Q) => $store.launch.questions.includes(Q.id)),
);

const setSelectableQuestion = () => {
    let selectedQuestion = launchingQuestions.value.find((Q) => Q.user_id !== $user.id) || launchingQuestions.value[0];
    $store.launch.user = (selectedQuestion?.user_id !== $user.id && selectedQuestion?.user_id) || null;
    questions.value.forEach(
        (Q) =>
            (Q.isSelectable =
                !Q.deleted_at &&
                (!selectedQuestion ||
                    !$store.launch.user ||
                    Q.user_id === selectedQuestion.user_id ||
                    Q.user_id === $user.id)),
    );
};
setSelectableQuestion();

const selectAll = () => {
    if (router.currentRoute.value.query.launch) {
        const hasOtherUserSelected = launchingQuestions.value.some((Q) => Q.user_id !== $user.id);

        if (!hasOtherUserSelected) {
            $store.launch.questions = questions.value
                .filter((Q) => !Q.deleted_at && Q.user_id === $user.id)
                .map((Q) => Q.id);
        } else {
            $store.launch.questions = questions.value.filter((Q) => Q.isSelectable).map((Q) => Q.id);
        }
        return;
    }

    $store.selectedQuestions = questions.value.filter((q) => q.user_id === $user.id).map((q) => q.id);
};

watchDebounced(search, applyFilters, {debounce: 500});
watch(params, getQuestions, {deep: true, immediate: true});
watch(sort, (value) => Object.assign(params, {page: 0, sort: value}));
watch(() => $store.launch.questions, setSelectableQuestion);

useEcho([
    {
        event: "Question\\QuestionDuplicated",
        callback: (e: {new_question: string}) => {
            getQuestion(e.new_question, params).then(({data}) => {
                const index = questions.value.findIndex((q: Question) => q.id === data.id);
                if (index !== -1) {
                    questions.value[index] = data;
                } else {
                    const isDescending = params.sort.startsWith("-");
                    const sortField = isDescending ? params.sort.substring(1) : params.sort;

                    const insertIndex = questions.value.findIndex((q: Question) => {
                        const valA = data[sortField as keyof Question];
                        const valB = q[sortField as keyof Question];

                        if (isDescending) {
                            return valA > valB;
                        }
                        return valA < valB;
                    });

                    if (insertIndex === -1) {
                        questions.value.push(data);
                    } else {
                        questions.value.splice(insertIndex, 0, data);
                    }
                }
            });
        },
    },
]);
</script>
<template>
    <PageContainer>
        <div class="relative mx-auto flex w-full grow flex-col pb-10">
            <QuestionsMassActions
                ref="massActions"
                v-if="router.currentRoute.value.query.select"
                :questions="questions"
                :state="state"
                :params="params"
                @cancel="cancel"
                @remove-questions="
                    (removedIds: string[]) => (questions = questions.filter((q) => !removedIds.includes(q.id)))
                "
            />
            <div class="" v-if="router.currentRoute.value.query.launch">
                <LaunchHeader class="mb-4" @cancel="cancel">
                    <template #header>
                        <div class="ml-auto flex items-center gap-3">
                            <p>Select posts</p>
                            <RouterLink
                                class="ml-auto flex-none"
                                :to="{name: 'questions.create', query: {launch: true}}"
                            >
                                <Button
                                    :icon="{icon: 'mdi:plus', width: '20'}"
                                    rounded
                                    class="ml-auto border bg-stone-50 !py-1 !pl-2 !pr-3 text-xs text-stone-700 shadow-sm hover:bg-stone-100"
                                >
                                    Or create one
                                </Button>
                            </RouterLink>
                        </div>
                    </template>
                </LaunchHeader>
            </div>
            <div class="mb-2">
                <PageTitle title="Posts" :show-back-button="false">
                    <div class="ml-auto flex gap-3">
                        <Button
                            v-if="!router.currentRoute.value.query.select && !router.currentRoute.value.query.launch"
                            class="bg-askr-green !px-2 !py-1 text-xs text-white hover:brightness-110 lg:text-sm"
                            size="small"
                            @click="select"
                        >
                            Select posts
                        </Button>
                        <Button
                            v-if="!router.currentRoute.value.query.launch && !router.currentRoute.value.query.select"
                            icon="mingcute:send-plane-fill"
                            class="bg-askr-green !px-2 !py-1 text-xs text-white hover:brightness-110 lg:text-sm"
                            size="small"
                            @click="launch"
                        >
                            Launch posts
                        </Button>
                        <div v-else class="flex items-center gap-2">
                            <div class="flex items-center gap-1.5">
                                <span class="text-sm">Selected</span>
                                <span
                                    class="min-w-8 rounded-full bg-askr-green px-2 py-1 text-center font-semibold text-white"
                                >
                                    {{
                                        router.currentRoute.value.query.launch
                                            ? $store.launch.questions.length
                                            : $store.selectedQuestions.length
                                    }}
                                </span>
                            </div>
                            <Button
                                v-tooltip.left="'Select all'"
                                :icon="{icon: 'material-symbols:select-all', width: 30}"
                                class="ml-auto h-8 w-8 flex-none text-askr-green"
                                @click="selectAll"
                            />
                            <Button
                                @click="deselectAll"
                                class="ml-auto h-8 w-8 flex-none text-red-600"
                                :icon="{icon: 'simple-line-icons:minus', width: 30}"
                            />
                        </div>
                        <RouterLink
                            v-if="!router.currentRoute.value.query.launch && !router.currentRoute.value.query.select"
                            :to="{name: 'questions.create', query: router.currentRoute.value.query}"
                        >
                            <Button
                                :icon="{icon: 'mdi:plus', width: '24'}"
                                rounded
                                class="pointer-events-auto h-9 w-9 bg-askr-green !p-0 text-white shadow-stone-600 hover:brightness-110"
                            />
                        </RouterLink>
                    </div>
                </PageTitle>
            </div>
            <div class="-sticky top-2 z-10 mb-3 flex items-center gap-2">
                <InputText type="search" v-model="search" placeholder="Search posts" class="grow" />
                <div class="relative">
                    <Button
                        :icon="{icon: 'iconoir:sort', width: 26}"
                        :rounded="false"
                        class="aspect-square h-full flex-none bg-askr-green !p-2.5 text-white hover:brightness-110"
                        @click="sortPanel.menu.toggle($event)"
                    />
                    <div
                        v-if="selectedSortOption"
                        class="pointer-events-none absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-red-500"
                    >
                        <Icon :icon="selectedSortOption.icon" class="h-3 w-3 text-white" />
                    </div>
                </div>
                <Menu ref="sortPanel" :popup="true" :model="sortMenuItems" />
            </div>
            <div class="flex items-stretch gap-6">
                <!-- filters -->
                <div class="hidden h-full w-full max-w-sm flex-none 2xl:block">
                    <div class="sticky top-2">
                        <QuestionsIndexFilters
                            v-model="filters"
                            :groups="groups"
                            :limits="state.limits"
                            :total-active-filters="totalActiveFilters"
                            @apply="applyFilters"
                            @clear="clearFilters"
                        />
                    </div>
                </div>
                <div class="grow">
                    <div
                        v-if="!loading && !questions?.length"
                        class="flex grow flex-col items-center justify-center gap-2 p-6 sm:h-auto"
                    >
                        <p class="mb-3 text-sm">
                            {{ search || totalActiveFilters ? "No matching posts found." : "You have no posts yet." }}
                        </p>
                        <RouterLink :to="{name: 'questions.create'}">
                            <Button
                                class="bg-askr-green text-white hover:brightness-110"
                                :icon="{
                                    icon: 'mdi:plus',
                                    width: '24',
                                }"
                            >
                                Create a new post
                            </Button>
                        </RouterLink>
                    </div>
                    <template v-else>
                        <template v-if="params.sort === 'title'">
                            <template v-for="[letter, questions] in questionsByLetter" :key="letter">
                                <AlphabetList :letter="letter" :vertical="true">
                                    <div class="my-2 grid grid-cols-2 gap-2 lg:grid-cols-3" v-auto-animate>
                                        <template v-for="question in questions" :key="question.id">
                                            <QuestionItem
                                                :question="question"
                                                :show-menu="question.user_id === $user.id"
                                                @toggleQuestionMenu="toggleQuestionMenu"
                                            />
                                        </template>
                                    </div>
                                </AlphabetList>
                            </template>
                        </template>
                        <template v-else>
                            <div class="grid grid-cols-2 gap-2 lg:grid-cols-3" v-auto-animate>
                                <template v-for="question in questions" :key="question.id">
                                    <QuestionItem
                                        :question="question"
                                        :show-menu="question.user_id === $user.id"
                                        @toggleQuestionMenu="toggleQuestionMenu"
                                    />
                                </template>
                            </div>
                        </template>
                    </template>
                    <PageLoading v-if="loading" class="pt-16" />
                </div>
            </div>
            <div v-if="!loading && !state.error && loadMore">
                <span ref="loadMoreIntersection"></span>
                <div class="flex justify-center">
                    <Button @click="params.page++" :loading="!!loading" :disabled="!!loading || !loadMore">
                        {{ loadMore || !questions.length ? "Load more" : "No more results" }}
                    </Button>
                </div>
            </div>
            <div
                class="pointer-events-none fixed bottom-10 left-0 right-0 z-50 mx-auto flex w-full max-w-3xl flex-col justify-center gap-2"
            >
                <div v-if="router.currentRoute.value.query.launch" class="flex justify-center">
                    <router-link
                        :to="{
                            name: 'questions.launch',
                            query: {...router.currentRoute.value.query},
                        }"
                    >
                        <Button
                            rounded
                            class="-shadow pointer-events-auto relative h-16 w-16 bg-askr-green text-white shadow-xl shadow-stone-600 lg:h-24 lg:w-24 dark:shadow-black"
                        >
                            <span
                                class="absolute top-2 flex items-center justify-center rounded-full bg-askr-green bg-stone-800 px-2 py-0.5 text-xs font-semibold text-white"
                            >
                                {{ $store.launch.questions.length }}
                            </span>
                            <div>
                                <Icon icon="mingcute:send-plane-fill" class="mt-1 h-8 w-8 lg:h-12 lg:w-12" />
                                <span class="text-xs font-semibold leading-none">LAUNCH</span>
                            </div>
                        </Button>
                    </router-link>
                </div>
            </div>
        </div>
        <FilterBar v-model="state.show.filters" :total-active-filters="totalActiveFilters">
            <RouterLink :to="{name: 'questions.create', query: router.currentRoute.value.query}">
                <Button
                    :icon="{icon: 'mdi:plus', width: '36'}"
                    rounded
                    class="pointer-events-auto h-14 w-14 bg-askr-green text-white shadow-stone-600"
                />
            </RouterLink>
        </FilterBar>
        <Dialog
            v-model:visible="showQuestionMenu"
            modal
            dismissable-mask
            class="max-w-sm"
            :closable="false"
            :show-header="false"
        >
            <template #container>
                <Menu :model="questionMenuItems" />
            </template>
        </Dialog>
        <Drawer
            v-model:visible="state.show.filters"
            position="bottom"
            header="Filters"
            class="max-h-dvh w-full max-w-3xl overflow-y-auto"
        >
            <div class="pb-12">
                <QuestionsIndexFilters
                    v-model="filters"
                    :groups="groups"
                    :limits="state.limits"
                    :total-active-filters="totalActiveFilters"
                    @apply="applyFilters"
                    @clear="clearFilters"
                />
            </div>
        </Drawer>
        <Dialog
            header="Duplicating post"
            modal
            :closable="false"
            :draggable="false"
            v-model:visible="state.loading.duplicatingQuestion"
            class="!max-w-48"
            :pt:title:class="`!hidden`"
        >
            <div class="relative">
                <ProgressSpinner class="aspect-square h-full w-full" />
                <div
                    class="absolute left-1/2 top-1/2 flex -translate-x-1/2 -translate-y-1/2 flex-col items-center text-xs"
                >
                    <span>Duplicating</span>
                    <span>post</span>
                </div>
            </div>
        </Dialog>
        <ShareQuestionDialog
            v-model:visible="state.show.shareDialog"
            :question="selectedQuestion"
            @update:visible="state.show.questionMenu = null"
            @sharing-started="handleSharingStarted"
        />
    </PageContainer>
</template>
<style scoped></style>
